import React from 'react';
import PropTypes from 'prop-types';
import autobind from 'class-autobind';
import classNames from 'classnames';
import { SvgLoader, SvgProxy } from 'react-svgmt';
import Draggable from 'react-draggable';

// Config
import { stylesMenuBlacklist as StylesBlacklist } from 'pages/Overview/config';


// Context
import { OverviewConsumer, OverviewContext } from 'pages/Overview/OverviewContext';


// Lodash
import { debounce, filter, get, map, sortBy } from 'lodash';


// CSS
import styles from './styles.css';


// Local config
const CONFIG = {
	scrollWait: 500,
	source: 'toolbar'
};


/**
 * Select menu list. Used for font family and style.
 *
 * @method Select
 * @param  {Array}    collection   Collection of items to build the list from.
 * @param  {String}   type         Styles or family. Each get a slightly different setup.
 * @return {Node}
 */
export default class Select extends React.Component {
	constructor(props) {
		super(props);
		autobind(this);

		this.state = {
			defaultPosition: null,
			dragging: false,
			focusedPane: 'family',
			isMobile: false,
			isScrolling: false,
			isScrollBottom: false,
			isScrollTop: true,
			position: null,
			styleBoundary: 60
		};

		this.menuPanel = React.createRef();

		this.scrollTimeout = null;

		this.onScroll = debounce(this.onScroll, 10); // eslint-disable-line no-magic-numbers
	}

	componentDidMount() {
		const {
			breakpoints,
			type
		} = this.props;

		const { styleBoundary } = this.state;

		// Set dimentions of menu for scroll listener. Wrapped in an rAF because
		// componentDidMount() completes before the DOM is rendered.
		requestAnimationFrame(() => {
			this.setMenuScrollHeight();
			this.setMenuOffsetHeight();
		});

		const isMobile = breakpoints.ltLargeMedium;

		const vpWidth = this.getVPWidth();

		this.setState({
			isMobile,
			defaultPosition: {
				...this.state.defaultPosition,
				x: type === 'style' ? vpWidth - styleBoundary : 0
			},
			position: {
				...this.state.position,
				x: type === 'style' ? vpWidth - styleBoundary : 0,
				y: 0
			}
		});
	}

	onScroll() {
		const { menuOffsetHeight, menuScrollHeight } = this.state;

		// Detect scroll position of menu.
		const menuScrollTop = this.menuPanel.current.scrollTop;
		const isScrollTop = menuScrollTop === 0;
		const isScrollBottom = menuScrollHeight - menuOffsetHeight === menuScrollTop;

		let state = {
			isScrollBottom,
			isScrollTop
		};

		if (this.state.isMobile) {
			if (this.scrollTimeout) {
				clearTimeout(this.scrollTimeout);
			}

			this.scrollTimeout = setTimeout(() => {
				this.scrollTimeout = null;
				this.setState({
					isScrolling: false
				});
			}, CONFIG.scrollWait);

			if (!this.state.isScrolling) {
				state = {
					...state,
					isScrolling: true
				};
			}
		}

		this.setState(state);
	}


	onStart() {
		this.setState({ dragging: true });
	}


	onDrag() {
		if (this.state.isScrolling) { return false; }
		return true;
	}


	onStop(event, data) {
		const { setActiveMobileMenu } = this.props;
		const { styleBoundary } = this.state;
		const vpWidth = this.getVPWidth();
		const vpCenter = vpWidth / 2;
		const x = data.x > vpCenter ? vpWidth - styleBoundary : styleBoundary;

		this.setState({
			...this.state,
			dragging: false,
			focusedPane: data.x > vpCenter ? 'family' : 'style',
			position: {
				...this.state.position,
				x: this.props.type === 'style' ? x : 0
			}
		});

		if (setActiveMobileMenu) {
			setActiveMobileMenu(data.x > vpCenter ? 'family' : 'style');
		}
	}


	getSelectItems(type, settings) {
		const collection =
			type === 'family'
				? this.props.collection
				: get(settings, 'selectedFamily.styles', null);

		if (!collection) { return null; }

		let menuItems = [];
		switch (type) {
			case 'family':
				menuItems = map(collection, article => ({
					filename: get(article, 'attributes.slug', null),
					id: get(article, 'id', null),
					name: get(article, 'attributes.name', null),
					type_capsule: get(article, 'attributes.type_capsule', null),
					sort_order: get(article, 'attributes.overview_sort_order', null)
				}));

				menuItems = menuItems.filter((item) => item.type_capsule === 0);

				menuItems = sortBy(menuItems, item => item.sort_order);

				break;
			case 'style': {
				// We need to check each item’s ID against the blacklist array in
				// config. Any item that has an ID in that array will be skipped.
				const whitelist = filter(collection, style => StylesBlacklist.indexOf(style.id) < 0);

				menuItems = map(whitelist, article => {
					const name = get(article, 'label', null);

					const filename = name
						.replace(/[^0-9a-zA-Z\s]/g, '') // Remove all non-alphanumeric characters
						.replace(/\s+/g, '-') // Replace spaces with a hyphen
						.toLowerCase()
						.replace('-gothic', '');

					return {
						group: get(article, 'group', null),
						family: get(article, 'family', null),
						filename,
						id: get(article, 'id', null),
						name
					};
				});

				break;
			}
			default:
				menuItems = null;
		}

		return menuItems;
	}


	setMenuScrollHeight() {
		if (get(this, 'menuPanel.current')) {
			this.setState({ menuScrollHeight: this.menuPanel.current.scrollHeight });
		}
	}


	setMenuOffsetHeight() {
		if (get(this, 'menuPanel.current')) {
			this.setState({ menuOffsetHeight: this.menuPanel.current.offsetHeight });
		}
	}


	getVPWidth() {
		return Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
	}


	/**
	 * Interior content for this component. This allows us to wrap mobile sizes
	 * in react-draggable for those sizes only. Desktop won’t load
	 * react-draggable at all.
	 *
	 * @method content
	 * @return {Node}  Inner content.
	 */
	content() {
		const {
			activeMobileMenu,
			breakpoints,
			closeMenu,
			isActive,
			type
		} = this.props;

		const {
			dragging,
			isScrollBottom,
			isScrollTop
		} = this.state;

		let currentGroup = null;

		const menuItems = this.getSelectItems(type, this.context.settings);

		return (
			<div
				className={
					classNames(
						styles.wrapper,
						styles[`wrapper--${type}`],
						{
							[styles['wrapper--active']]: isActive,
							[styles['wrapper--isDragging']]: dragging,
							[styles['wrapper--familyIsBlurred']]: activeMobileMenu === 'style' && type !== 'style' // When the active menu is 'styles' and type is 'family', we dim the family menu contents.
						}
					)
				}
			>
				{ !isScrollTop && <div className={ styles['shadow--top'] } /> }
				{ !isScrollBottom && <div className={ styles['shadow--bottom'] } /> }
				<OverviewConsumer>
					{ ({ getProductFeatures, putSetting, settings }) =>
						<ul
							className={ styles[`selectList--${type}`] }
							onScroll={ this.onScroll }
							ref={ this.menuPanel }
						>
							{ map(menuItems, (article) => {
								const id = parseInt(get(article, 'id'), 10);
								const name = get(article, 'name');
								const isSelected =
									type === 'style'
										? id === get(settings, 'selectedStyle.id')
										: parseInt(get(settings, 'selectedFamily.id', 0), 10) === parseInt(id, 10);

								const filename = get(article, 'filename').toLowerCase();
								const path = type === 'family'
									? `/images/fonts/families/${filename}.svg`
									: `/images/fonts/styles/optimized/${filename}.svg`;

								const content = (
									<li
										className={
											classNames({
												[styles.selectListItem]: true,
												[styles['selectListItem--withRule']]: currentGroup !== null && article.group !== currentGroup,
												[styles['selectListItem--active']]: isSelected
											})
										}
										key={ id }
										onMouseDown={ async event => {
											event.preventDefault();
											// then set the family
											if (type === 'family') {
												// load the productFeatures file if it hasn’t already been loaded
												await getProductFeatures(id);

												await putSetting({
													type: 'SET_FAMILY',
													id,
													processDOM: false,
													silent: false,
													source: CONFIG.source
												});

												await putSetting({
													type: 'SET_STYLE',
													processDOM: true,
													silent: false,
													source: CONFIG.source
												});
											} else {
												await putSetting({
													type: 'SET_STYLE',
													familyID: settings.selectedFamily.id,
													processDOM: true,
													silent: false,
													source: CONFIG.source,
													styleID: id
												});
											}

											closeMenu();
										} }
										role="presentation"
									>
										<SvgLoader path={ path }>
											<SvgProxy
												className={ styles.selectListImage }
												fill="rgb(245, 245, 245)"
												selector="#Layer_1"
											/>
										</SvgLoader>
										<div className={ styles.selectLabel }>{ name }</div>
										{ type === 'family' && isSelected && breakpoints.ltLargeMedium &&
											<div className={ styles.pointer } />
										}
									</li>
								);

								// Update group with current value
								currentGroup = article.group || null;

								return content;
							}) }
						</ul>
					}
				</OverviewConsumer>
			</div>
		);
	}


	render() {
		const { type } = this.props;

		const {
			defaultPosition,
			isMobile,
			position,
			styleBoundary
		} = this.state;

		const vpWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);

		const dragSettings = {
			bounds: {
				top: 0,
				left: type === 'style' ? styleBoundary : 0,
				right: type === 'style' ? vpWidth - styleBoundary : 0,
				bottom: 0
			},
			defaultPosition,
			position
		};

		if (isMobile && dragSettings.defaultPosition.x) {
			return (
				<Draggable
					{ ...dragSettings }
					axis="x"
					onDrag={ this.onDrag }
					onStart={ this.onStart }
					onStop={ this.onStop }
				>
					{ this.content() }
				</Draggable>
			);
		}

		return this.content();
	}
}

Select.contextType = OverviewContext;

Select.propTypes = {
	activeMobileMenu: PropTypes.string,
	breakpoints: PropTypes.object.isRequired,
	closeMenu: PropTypes.func,
	collection: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	isActive: PropTypes.bool.isRequired,
	setActiveMobileMenu: PropTypes.func,
	type: PropTypes.string.isRequired
};

Select.defaultProps = {
	activeMobileMenu: null,
	closeMenu: null,
	collection: null,
	setActiveMobileMenu: null
};
